import React from 'react';
import PropTypes from 'prop-types';
import { List } from 'immutable';
import { compose, withProps, withHandlers } from 'recompose';

import * as timeplanUnitStyles from './timeplanUnitStyles.css';
import TimeplanUnit from './TimeplanUnit';
import { createNewTimeplanUnit } from '../../../lib/creators';
import { ButtonSmall } from '../../../components/Button';
import defaultWorkType from './defaultWorkType';
import setTimeplanUnit from './setTimeplanUnit';
import { getDate } from '../../../lib/dates';
import SummarizedDay from './SummarizedDay';
import withResource from '../../../containers/withResource';
import MissingReportedTimes from './MissingReportedTimes';
import addTimeplanUnits from './addTimeplanUnits';
import { MileageAllowanceRecord } from '../../../entities/records';
import { useTranslation } from 'react-i18next';

const Resource = withResource(({ resource: { name } }) => <span>{name}</span>);

function TimeplanDay({
  disabled,
  date,
  resources,
  timeplanUnits,
  workTypes,
  absentReasons,
  onChangeTimeplanUnits,
  isEditable,
  compareTimeplanUnits,
  duplicates,
  overlaps,
  bookedTimeplanUnits,
  resourceReportedTimeplanUnits,
  reportedTimeplanUnits,
  isNotReported,
  addTimeplanUnit,
  ...props
}) {
  const { t } = useTranslation();
  return (
    <div className={timeplanUnitStyles.timeplanDay}>
      {!isEditable && (
        <SummarizedDay
          workTypes={workTypes}
          timeplanUnits={timeplanUnits}
          compareTimeplanUnits={compareTimeplanUnits}
          overlaps={overlaps}
          duplicates={duplicates}
          isNotReported={isNotReported}
          absentReasons={absentReasons}
        />
      )}
      {isEditable && timeplanUnits.map(timeplanUnit => (
        <TimeplanUnit
          key={timeplanUnit.id}
          isEditable={isEditable}
          disabled={disabled}
          resources={resources}
          timeplanUnit={timeplanUnit}
          date={getDate(date)}
          overlaps={!!overlaps[timeplanUnit.id]}
          duplicates={!!duplicates[timeplanUnit.id]}
          workTypes={workTypes}
          absentReasons={absentReasons}
          invalid={false}
          onChangeTimeplanUnits={onChangeTimeplanUnits}
          timeplanUnits={timeplanUnits}
          {...props}
        />
      ))}
      {isEditable && !disabled && (
        <div>
          <ButtonSmall
            onClick={addTimeplanUnit}
            icon="plus-circle"
            stretch
          >
            {t("Lägg till arbetspass")}
          </ButtonSmall>
          {bookedTimeplanUnits.size > 0 && (
            <div className="text-danger">
              <b>{t("Redan bokade tider")}</b>
              {bookedTimeplanUnits.map(({ id, from, to, resourceId }) => (
                <div key={id}>
                  {`${from}-${to}`} (<Resource resourceId={resourceId} />)
                </div>
              ))}
            </div>
          )}
          {resourceReportedTimeplanUnits.size > 0 && (
            <div className="text-danger">{t("Tidrapporterad på denna bokning")}</div>
          )}
          {reportedTimeplanUnits.size > 0 && (
            <div className="text-danger">{t("Attesterad på denna bokning")}</div>
          )}
          <MissingReportedTimes isNotReported={isNotReported} />
        </div>
      )}
    </div>
  );
}

TimeplanDay.defaultProps = {
  bookedTimeplanUnits: new List(),
  resourceReportedTimeplanUnits: new List(),
  reportedTimeplanUnits: new List(),
};

TimeplanDay.propTypes = {
  disabled: PropTypes.bool.isRequired,
  date: PropTypes.string.isRequired,
  onChangeTimeplanUnits: PropTypes.func.isRequired,
  resources: PropTypes.instanceOf(List).isRequired,
  timeplanUnits: PropTypes.instanceOf(List).isRequired,
  workTypes: PropTypes.instanceOf(List).isRequired,
  absentReasons: PropTypes.instanceOf(List).isRequired,
  isEditable: PropTypes.bool.isRequired,
  compareTimeplanUnits: PropTypes.instanceOf(List).isRequired,
  bookedTimeplanUnits: PropTypes.instanceOf(List),
  resourceReportedTimeplanUnits: PropTypes.instanceOf(List),
  reportedTimeplanUnits: PropTypes.instanceOf(List),
  isNotReported: PropTypes.bool.isRequired,
  addTimeplanUnit: PropTypes.func.isRequired,
};

export default compose(
  withProps(({ compareTimeplanUnits, timeplanUnits }) => ({
    isNotReported: compareTimeplanUnits
      && compareTimeplanUnits.size > 0 && timeplanUnits.size === 0,
  })),
  withHandlers({
    addTimeplanUnit: ({
      resources,
      onChangeTimeplanUnits,
      date,
      workTypes,
      hasMileageAllowance,
      hasMileageAllowanceOnDate,
    }) => () => {
      const resourceId = resources.size === 1 ? resources.getIn(['0', 'id']) : null;
      const newTimeplanUnit = createNewTimeplanUnit(
        getDate(date),
        resourceId,
        defaultWorkType(workTypes).id,
        '',
        '',
      );
      if (!hasMileageAllowanceOnDate && hasMileageAllowance) {
        onChangeTimeplanUnits(
          addTimeplanUnits,
          [
            newTimeplanUnit,
            new MileageAllowanceRecord({
              date,
              kilometers: 0,
            }),
          ],
        );
      } else {
        onChangeTimeplanUnits(
          setTimeplanUnit,
          newTimeplanUnit,
          {},
        );
      }
    },
  }),
)(TimeplanDay);
